﻿import {
    Box,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIButton } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import { CustomerCommand } from '../../../../services/command';
import { CustomerQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { RPTH, rpthCls } from './rptHistoryComponent.classes';
import {
    formatSelectedSubTopicDisplay,
    handleSubTopicSelectionChange,
    isAllSubTopicsSelected,
    getRptHistoryRowClassName,
    isRptTimeRuleViolated,
    mapRptOrdersToGridRows,
    RPT_HISTORY_ROW_VIOLATION_CLASS,
    RPT_SUBTOPIC_ALL,
    RptOrderGridRow,
    RptSubTopicOption,
} from './rptHistoryComponent.helpers';

interface IProps {
    datas?: any;
}
export const RptHistoryComponent: FC<IProps> = ({ datas }) => {
    const [rptSubTopic, setRptSubTopic] = useState<RptSubTopicOption[]>();
    const { t } = useTranslation();
    const [selectedSubTopicCodes, setSelectedSubTopicCodes] = useState<string[]>([]);
    const [text, setText] = useState<any>('');
    const [rptOrder, setRptOrder] = useState<RptOrderGridRow[]>([]);
    const [rptRuleViolated, setRptRuleViolated] = useState(false);
    const [pageSize, setPageSize] = useState(5);
    const GetRptTopic = async () => {
        try {
            CustomerQuery.GetRptSubTopics().then((res: any) => {
                if (res.data) {
                    const parseData = res.data.map((item: any) => {
                        return {
                            value: item.code,
                            name: item.name,
                        };
                    });
                    setRptSubTopic(parseData && [...parseData]);
                } else {
                    Swal.fire(t('general.serviceError'), res.errorMessage, 'error');
                }
            });
        } catch (error: any) {
            Swal.fire(t('general.error'), error.toString(), 'error');
        }
    };
    const GetRptOrders = () => {
        if (selectedSubTopicCodes.length === 0) {
            Swal.fire(t('customerInformation.rptSelectContactReason'), ' ', 'error');
            return;
        }

        const params = {
            customerNo: datas.customer.customerId,
            subTopicCode: selectedSubTopicCodes,
        };

        CustomerCommand.GetRptOrders(params)
            .then((res: any) => {
                if (res) {
                    const orders = Array.isArray(res) ? res : [];
                    const violated = isRptTimeRuleViolated(orders);

                    setRptRuleViolated(violated);
                    setText(
                        t(violated ? 'customerInformation.rptRuleNotAllowed' : 'customerInformation.rptRuleAllowed')
                    );
                    setRptOrder(mapRptOrdersToGridRows(orders, TimeHelper.formatDateHour));
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };
    useEffect(() => {
        GetRptTopic();
    }, []);
    return (
        <>
            <Grid className={RPTH.filtersRow} container spacing={2} sx={{ margin: '1rem' }}>
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                        xl: 6,
                    }}
                >
                    <FormControl className={RPTH.fieldTopic} fullWidth sx={{ width: '100%', maxWidth: 550 }}>
                        <InputLabel id="labelApp">{t('customerInformation.rptContactReason')}</InputLabel>
                        <Select
                            className={RPTH.inputSelectTopic}
                            label={t('customerInformation.rptReason')}
                            labelId="labelApp"
                            id="parentMenuLabel"
                            multiple
                            value={selectedSubTopicCodes}
                            onChange={(event) => {
                                setSelectedSubTopicCodes(
                                    handleSubTopicSelectionChange(
                                        event.target.value,
                                        selectedSubTopicCodes,
                                        rptSubTopic
                                    )
                                );
                            }}
                            renderValue={(selected) =>
                                formatSelectedSubTopicDisplay(selected as string[], rptSubTopic, t('general.all'))
                            }
                            sx={{
                                '& .MuiSelect-select': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        >
                            <MenuItem value={RPT_SUBTOPIC_ALL}>
                                <Checkbox checked={isAllSubTopicsSelected(selectedSubTopicCodes, rptSubTopic)} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {rptSubTopic?.map((comp) => (
                                <MenuItem key={comp.value} value={comp.value}>
                                    <Checkbox checked={selectedSubTopicCodes.indexOf(comp.value) > -1} />
                                    <ListItemText primary={comp.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid
                    className={RPTH.filterActions}
                    sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                        xl: 6,
                    }}
                >
                    <SCUIButton
                        className={RPTH.inputButtonSearch}
                        text={t('general.search')}
                        type="secondary"
                        sizeType="fixed"
                        size={'m'}
                        style={{ height: '35px' }}
                        onClick={() => {
                            setRptOrder([]);
                            setText('');
                            setRptRuleViolated(false);
                            GetRptOrders();
                        }}
                    />
                </Grid>
            </Grid>
            <Grid
                className={RPTH.resultsSection}
                sx={{ padding: '8px' }}
                size={{
                    xl: 12,
                    lg: 12,
                    md: 12,
                    xs: 12,
                }}
            >
                {text.length > 0 && (
                    <>
                        <Box className={RPTH.resultBanner} sx={{ pb: 1 }}>
                            <Typography
                                component="p"
                                sx={{
                                    m: 0,
                                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                }}
                            >
                                {t('customerInformation.rptResultLabel')}{' '}
                                <Box
                                    component="span"
                                    className={rpthCls(
                                        RPTH.resultStatusHighlight,
                                        rptRuleViolated
                                            ? 'rpt-history-field--result-status-highlight--blocked'
                                            : 'rpt-history-field--result-status-highlight--allowed'
                                    )}
                                    sx={{
                                        fontSize: 'inherit',
                                        fontWeight: 'inherit',
                                        lineHeight: 'inherit',
                                        color: rptRuleViolated ? 'error.main' : 'rgb(7, 252, 3)',
                                    }}
                                >
                                    {text}
                                </Box>
                            </Typography>
                            {rptRuleViolated && (
                                <Typography
                                    className={RPTH.resultRowHint}
                                    component="p"
                                    sx={{
                                        m: 0,
                                        pt: 0.5,
                                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                        lineHeight: 1.5,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {t('customerInformation.rptRuleRowHighlightHint')}
                                </Typography>
                            )}
                        </Box>
                        <Box className={RPTH.datagridWrap} sx={{ height: '100%', width: '100%' }}>
                            <Box className={RPTH.datagrid}>
                                <SCUICustomDataGrid
                                    randomId
                                    rows={rptOrder}
                                    getRowClassName={(params: any) =>
                                        getRptHistoryRowClassName(params.row as RptOrderGridRow)
                                    }
                                    sx={{
                                        [`& .${RPT_HISTORY_ROW_VIOLATION_CLASS}`]: {
                                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                            borderLeft: '4px solid #d32f2f',
                                        },
                                        [`& .${RPT_HISTORY_ROW_VIOLATION_CLASS}:hover`]: {
                                            backgroundColor: 'rgba(211, 47, 47, 0.14)',
                                        },
                                    }}
                                    columns={[
                                        {
                                            field: 'customerNo',
                                            headerName: t('customerInformation.customerNo'),
                                            flex: 1,
                                        },
                                        { field: 'orderNo', headerName: t('general.orderNo'), flex: 1 },
                                        {
                                            field: 'createdOn',
                                            headerName: t('customerInformation.createdDate'),
                                            flex: 1,
                                        },
                                        {
                                            field: 'rptOrderNo',
                                            headerName: t('customerInformation.rptOrderNumber'),
                                            flex: 1,
                                        },
                                        {
                                            field: 'subTopicTR',
                                            headerName: t('customerInformation.rptContactReason'),
                                            flex: 1,
                                        },
                                    ]}
                                    pageSize={pageSize}
                                    onPageSizeChange={(i: number) => {
                                        setPageSize(i);
                                    }}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </Grid>
        </>
    );
};
